import { gql, useQuery } from "@apollo/client";
import * as Popover from "@radix-ui/react-popover";
import * as Tooltip from "@radix-ui/react-tooltip";
import { SetStateAction, useEffect, useMemo, useState } from "react";
import Icon from "src/components/Icon";
import InnerLoading from "src/components/InnerLoading";
import {
  FilterInvitationList,
  NumberOfGuestsByCategory,
} from "src/graphql/queries";
import InviteListCard from "./InviteListCard";
import styles from "./styles.module.scss";
// import Pagination from "src/components/Pagination";
import toast from "react-hot-toast";
import { SearchInput } from "src/components/Input";
import {
  filterInvitationListBy,
  filterInvitationListByVariables,
  invitationListFilter,
  noGuestPerCategory,
} from "src/types/api.d";
import { v4 as uuidv4 } from "uuid";
// import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
// import UploadCSV from "./modals/UploadCSV";
import { useNavigate } from "react-router-dom";
import Button from "src/components/Button";
import Dialog from "src/components/Dialog";
import { Spinner } from "src/components/Spinner";
import useAppStore from "src/store/utils";
import SendInvitesOptionsModal from "../AddGuests/AddGuestsSpreadsheet/modals/SendInvite";
import EmptyState from "./EmptyState";

function InviteList() {
  // const [csvModal, setCsvModal] = useState<boolean>(false);
  const [, setTotalPages] = useState(1);
  // const [startPage, setStartPage] = useState(1);
  const [pageSize] = useState(500);
  const [page] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [show, setShow] = useState(false);
  const [selectedIDs, setSelectedIDs] = useState<string[]>([]);
  const [openInviteModal, setOpenInviteModal] = useState<boolean>(false);
  const { event } = useAppStore((state) => state);
  const navigate = useNavigate();

  const [filter] = useState<invitationListFilter>(
    invitationListFilter.all ||
      invitationListFilter.not_sent ||
      invitationListFilter.sent
  );

  useEffect(() => {
    if (!show) {
      setSelectedIDs([]);
    }
  }, [show]);

  const { data: inviteList, loading: invitationListLoading } = useQuery<
    filterInvitationListBy,
    filterInvitationListByVariables
  >(FilterInvitationList, {
    variables: {
      eventId: event.id,
      filter: filter,
      page: page,
      pageSize: pageSize,
    },
    skip: !event.id,
    fetchPolicy: "cache-and-network",
    // fetchPolicy: "network-only",
    onCompleted(response) {
      if (
        !!response?.filterInvitationListBy &&
        response?.filterInvitationListBy.length
      ) {
        setTotalPages(
          Math.floor(response?.filterInvitationListBy.length / pageSize)
        );
      }
    },
    onError(error) {
      if (error?.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occured"}</p>
        );
      } else {
        navigate("/clear");
      }
    },
  });

  const results = useMemo(() => {
    return (
      inviteList?.filterInvitationListBy?.filter(
        (guest) =>
          guest?.first_name
            .toLocaleLowerCase()
            .includes(searchValue.toLocaleLowerCase()) ||
          guest?.last_name
            .toLocaleLowerCase()
            .includes(searchValue.toLocaleLowerCase())
      ) ?? []
    );
  }, [searchValue, inviteList]);

  const { data: categories, loading: categoryListLoading } =
    useQuery<noGuestPerCategory>(NumberOfGuestsByCategory, {
      variables: {
        eventId: event.id,
      },
      fetchPolicy: "cache-and-network",
      onError(error) {
        if (error?.message !== "unauthenticated") {
          toast.error(
            <p className="toast">{error?.message ?? "An error occured"}</p>
          );
        } else {
          navigate("/clear");
        }
      },
    });

  const { data } = useQuery(
    gql`
      query event($eventId: Int!) {
        event(event_id: $eventId) {
          invite_credit {
            whatsapp
          }
        }
      }
    `,
    {
      variables: { eventId: event.id },
      fetchPolicy: "cache-and-network",
    }
  );

  const inviteCredit = useMemo(
    () => data?.event?.invite_credit.whatsapp,
    [data?.event?.invite_credit.whatsapp]
  );

  const guestCount = useMemo(() => {
    const res = categories?.noGuestPerCategory?.reduce((acc, curr) => {
      if (curr?.num_guest) {
        return acc + curr?.num_guest;
      } else {
        return acc;
      }
    }, 0);
    return res;
  }, [categories]);

  // const isLoading =
  //   (invitationListLoading || categoryListLoading) &&
  //   (!inviteList || !categories);

  const inviteListData = inviteList?.filterInvitationListBy ?? [];
  const hasInviteListData = inviteListData.length > 0;

  return (
    <div
      className={`transition-all duration-500 ease-in-out ${styles["invite-list"]} ${!invitationListLoading && hasInviteListData ? `translate-y-0 opacity-100` : `translate-y-4 opacity-80`}`}
    >
      {(invitationListLoading || categoryListLoading) && !hasInviteListData && (
        <div className={styles["invite-list__loading"]}>
          <InnerLoading />
        </div>
      )}

      {invitationListLoading && hasInviteListData && (
        <div className="flex w-full items-center justify-center py-2">
          <Spinner />
        </div>
      )}

      {hasInviteListData && (
        <>
          <header className={styles["invite-list__header"]}>
            <div className={styles["invite-list__header--search-container"]}>
              <div className={styles["invite-list__header--search"]}>
                <div className={styles["search-input"]}>
                  <SearchInput
                    icon={<Icon iconName="search" />}
                    name="Search Guests"
                    placeholder="Search Guests"
                    value={searchValue}
                    onChange={(e: {
                      target: { value: SetStateAction<string> };
                    }) => setSearchValue(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className={styles["invite-list__header__button"]}>
              {/* <DropdownMenu.Root>
                <DropdownMenu.Trigger
                  className={styles["invite-list__header__icon"]}
                >
                  <Icon iconName="filter" />
                  <span className="visually-hidden">open dropdown</span>
                </DropdownMenu.Trigger>

                <DropdownMenu.Content className={styles["card-dropdown-list"]}>
                  <DropdownMenu.Item
                    onClick={() => {
                      setFilter(invitationListFilter?.all);
                    }}
                    // className={styles["card-dropdown-list-edit"]}
                  >
                    All
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                  onClick={() => {
                    setFilter(invitationListFilter?.sent);
                  }}
                  // className={styles["card-dropdown-list-delete"]}
                  >
                    Invited
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                  onClick={() => {
                    setFilter(invitationListFilter?.not_sent);
                  }}
                  // className={styles["card-dropdown-list-delete"]}
                  >
                    Not Invited
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root> */}

              <Popover.Root>
                <Popover.Trigger className="flex sm:hidden">
                  <div
                    className={`${styles["count"]} cursor-pointer transition duration-500 hover:bg-gray-200`}
                  >
                    <Icon iconName="coin" />
                    <span>{inviteCredit}</span>
                  </div>
                </Popover.Trigger>

                <Popover.Content sideOffset={10}>
                  <div className="flex flex-col rounded-[6px] border border-dashed border-primary-5 bg-primary-4 px-[1.375rem] py-[.5rem]">
                    <div className="flex w-[200px] flex-col gap-1">
                      <h6 className="text-[13px] font-[600] leading-[19px] -tracking-[0.02em] text-dark-1">
                        Invite Credit
                      </h6>
                      <p className="text-[14px] leading-[21px] -tracking-[0.02em] text-secondary-3">
                        Invite credits let you send invites without paying each
                        time. We refund failed invites as credits, or you can
                        buy more anytime.
                      </p>
                    </div>

                    <Popover.Close>
                      <button className="text-sm underline">Close</button>
                    </Popover.Close>
                  </div>
                </Popover.Content>
              </Popover.Root>

              <Tooltip.Provider delayDuration={300}>
                <Tooltip.Root>
                  <Tooltip.Trigger className="hidden sm:flex">
                    <div
                      className={`${styles["count"]} cursor-pointer transition duration-500 hover:bg-gray-200`}
                    >
                      <Icon iconName="coin" />
                      <span>{inviteCredit}</span>
                    </div>
                  </Tooltip.Trigger>

                  <Tooltip.Content side="bottom">
                    <div className="flex flex-col gap-[1.625rem] rounded-[6px] border border-dashed border-primary-5 bg-primary-4 px-[1.375rem] py-[.5rem]">
                      <div className="flex w-[200px] flex-col gap-1">
                        <h6 className="text-[13px] font-[600] leading-[19px] -tracking-[0.02em] text-dark-1">
                          Invite Credit
                        </h6>
                        <p className="text-[14px] leading-[21px] -tracking-[0.02em] text-secondary-3">
                          Invite credits let you send invites without paying
                          each time. We refund failed invites as credits, or you
                          can buy more anytime.
                        </p>
                      </div>
                    </div>

                    <Tooltip.Arrow className={`fill-primary-5`} />
                  </Tooltip.Content>
                </Tooltip.Root>
              </Tooltip.Provider>

              <div className={styles["count"]}>
                <Icon iconName="twoUsers" />
                <span>{guestCount}</span>
              </div>
              <div>
                <Dialog
                  open={openInviteModal}
                  onOpenChange={setOpenInviteModal}
                  trigger={
                    <Button
                      label="Resend"
                      design="secondary"
                      disabled={selectedIDs.length < 1}
                      type="button"
                    >
                      Resend
                    </Button>
                  }
                >
                  <SendInvitesOptionsModal
                    invitationFilter={filter}
                    noOfGuests={selectedIDs?.length ?? 0}
                    invitationIDs={selectedIDs}
                    setOpenInviteModal={setOpenInviteModal}
                  />
                </Dialog>
              </div>

              <div>
                <Button
                  type="button"
                  label={show ? "Unselect" : "Select"}
                  design="secondary"
                  onClick={() => {
                    setShow(!show);
                  }}
                >
                  <span> {show ? "Unselect" : "Select"} </span>
                </Button>
              </div>
            </div>
          </header>

          <main className={styles["invite-list__main"]}>
            <div className={styles["invite-list__table"]}>
              <div className="flex flex-col gap-3 pb-7">
                {results?.map((list) => (
                  <InviteListCard
                    key={uuidv4()}
                    invitee={list}
                    filter={filter}
                    page={page}
                    pageSize={pageSize}
                    categories={categories}
                    setShow={setShow}
                    show={show}
                    selectedIDs={selectedIDs}
                    setSelectedIDs={setSelectedIDs}
                  />
                ))}
              </div>
            </div>
            {/*
            <Pagination
              pageSize={pageSize}
              startPage={startPage}
              setPageSize={setPageSize}
              setStartPage={setStartPage}
              totalPages={totalPages}
              page={page}
              setPage={setPage}
            />
              */}
          </main>
        </>
      )}

      {!invitationListLoading && !hasInviteListData && (
        <EmptyState categories={categories} page={page} pageSize={pageSize} />
      )}
    </div>
  );
}

export default InviteList;
